"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, TrendingUp, CloudRain, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Dynamic Corporate vs. Weekend Pace",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Balance Monday-to-Thursday high-ADR corporate travelers in BKC, Lower Parel, and Powai with weekend leisure staycations. We engineer dynamic rate fences and minimum stay rules to sustain 85%+ blended weekly occupancy.",
  metricLabel: "Strategy focus",
  metricValue: "Midweek corporate yield & weekend staycation packages"
}, {
  id: 2,
  title: "Aggressive Comp-Set Rate Volatility",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across Booking.com, Agoda, and MakeMyTrip across airport and submarket clusters. Automated competitor triggers protect your RevPAR during high-compression expos, concerts, and cricket matches.",
  metricLabel: "Strategy focus",
  metricValue: "Event-driven compression yield & intraday OTA monitoring"
}, {
  id: 3,
  title: "OTA Dependency & Commission Leakage",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Reclaim high profit margins eroded by 18-25% OTA commissions. We establish watertight rate parity across Google Hotels and direct booking engines while capturing corporate direct loyalty.",
  metricLabel: "Strategy focus",
  metricValue: "Direct brand booking boost & B2B rate parity control"
}];
const seasonalityPeriods = [{
  period: "Oct - Mar",
  title: "Peak Corporate & Events",
  description: "Peak business travel, weddings, international transit, and major conventions. High ADR yield and strict cancellation policies.",
  tag: "High Yield Season"
}, {
  period: "Apr - Jun",
  title: "Summer Shoulder & Events",
  description: "Transition to domestic leisure, corporate offsites, and IPL event spikes. Dynamic unbundled packages keep RevPAR resilient.",
  tag: "Shoulder Season"
}, {
  period: "Jul - Sep",
  title: "Monsoon & Local Staycations",
  description: "Domestic corporate travel remains steady while leisure shifts to weekend staycations and MICE banquet revenues.",
  tag: "Value Preservation"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapemumbai" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Mumbai
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Mumbai represents India&apos;s financial engine and most competitive hospitality market—characterized by rapid booking turnarounds, high corporate density across key business districts, airport transit demand, and massive event compression. Sunil Singh implements disciplined, algorithm-backed revenue strategies tailored to metropolitan hotels and boutique properties.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full rounded-xl border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Mumbai Seasonality & Demand Cycles Block */}
        <div className="mt-14 md:mt-20 p-6 md:p-8 rounded-lg border border-border bg-muted/30">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-6 border-b border-border/60">
            <div>
              <span className="text-xs font-mono uppercase tracking-wider text-secondary font-semibold">
                Demand Calendar Breakdown
              </span>
              <h3 className="font-serif text-2xl font-bold text-foreground mt-1">
                Mumbai Market Seasonality & Demand Cycles
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Managing inventory velocity and rate fences across Mumbai&apos;s primary commercial and event cycles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityPeriods.map((cycle, i) => <div key={cycle.period} data-index={i} className="rounded-md border border-border/70 bg-card p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-secondary/15 text-secondary-foreground">
                    {cycle.period}
                  </span>
                  <span className="text-xs text-muted-foreground font-medium">
                    {cycle.tag}
                  </span>
                </div>
                <h4 className="font-semibold text-base text-foreground">
                  {cycle.title}
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {cycle.description}
                </p>
              </div>)}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Mumbai hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}